import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Cookie } from "lucide-react";
import { getConsent, saveConsent, OPEN_BANNER_EVENT, DEFAULT_CONSENT } from "@/lib/cookieConsent";

export const CookieConsentBanner = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [prefs, setPrefs] = useState({ ...DEFAULT_CONSENT });

  useEffect(() => {
    // Defer banner reveal until after LCP window so it never competes with
    // the hero for paint budget and never lands mid-load-shift. It's `position: fixed`,
    // so revealing it later doesn't push page content.
    let timer: number | undefined;
    if (!getConsent()) {
      const schedule = (cb: () => void) => {
        const w = window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
        if (typeof w.requestIdleCallback === "function") {
          w.requestIdleCallback(cb, { timeout: 3500 });
        } else {
          timer = window.setTimeout(cb, 2500);
        }
      };
      schedule(() => setVisible(true));
    }
    const open = () => {
      const current = getConsent();
      if (current) {
        setPrefs({
          necessary: true,
          preferences: current.preferences,
          analytics: current.analytics,
          marketing: current.marketing,
        });
      }
      setCustomOpen(true);
    };
    window.addEventListener(OPEN_BANNER_EVENT, open);
    return () => {
      window.removeEventListener(OPEN_BANNER_EVENT, open);
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  const acceptAll = () => {
    saveConsent({ necessary: true, preferences: true, analytics: true, marketing: true });
    setVisible(false);
    setCustomOpen(false);
  };
  const rejectAll = () => {
    saveConsent({ necessary: true, preferences: false, analytics: false, marketing: false });
    setVisible(false);
    setCustomOpen(false);
  };
  const savePrefs = () => {
    saveConsent(prefs);
    setVisible(false);
    setCustomOpen(false);
  };

  return (
    <>
      {visible && (
        <div
          role="dialog"
          aria-live="polite"
          aria-label={t("cookies.title")}
          className="fixed bottom-0 inset-x-0 z-[60] p-3 sm:p-4 md:p-6 pointer-events-none"
        >
          <div className="mx-auto max-w-4xl pointer-events-auto rounded-xl border bg-card text-card-foreground shadow-2xl p-4 sm:p-5 flex flex-col md:flex-row gap-4 md:items-center">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="shrink-0 w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Cookie className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold mb-1">{t("cookies.title")}</div>
                <p className="text-sm text-muted-foreground">
                  {t("cookies.message")}{" "}
                  <Link to="/legal/privacy" className="underline hover:text-foreground">
                    {t("cookies.learnMore")}
                  </Link>
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 md:flex-nowrap shrink-0">
              <Button variant="outline" size="sm" onClick={() => setCustomOpen(true)}>
                {t("cookies.customize")}
              </Button>
              <Button variant="ghost" size="sm" onClick={rejectAll}>
                {t("cookies.rejectAll")}
              </Button>
              <Button size="sm" onClick={acceptAll}>
                {t("cookies.acceptAll")}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("cookies.customizeTitle")}</DialogTitle>
            <DialogDescription>{t("cookies.customizeDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <CategoryRow
              title={t("cookies.cat.necessary")}
              desc={t("cookies.cat.necessaryDesc")}
              checked
              disabled
              onChange={() => {}}
            />
            <CategoryRow
              title={t("cookies.cat.preferences")}
              desc={t("cookies.cat.preferencesDesc")}
              checked={prefs.preferences}
              onChange={(v) => setPrefs((p) => ({ ...p, preferences: v }))}
            />
            <CategoryRow
              title={t("cookies.cat.analytics")}
              desc={t("cookies.cat.analyticsDesc")}
              checked={prefs.analytics}
              onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
            />
            <CategoryRow
              title={t("cookies.cat.marketing")}
              desc={t("cookies.cat.marketingDesc")}
              checked={prefs.marketing}
              onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={rejectAll}>{t("cookies.rejectAll")}</Button>
            <Button variant="outline" onClick={acceptAll}>{t("cookies.acceptAll")}</Button>
            <Button onClick={savePrefs}>{t("cookies.savePrefs")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const CategoryRow = ({
  title, desc, checked, disabled, onChange,
}: { title: string; desc: string; checked: boolean; disabled?: boolean; onChange: (v: boolean) => void }) => (
  <div className="flex items-start justify-between gap-4 border rounded-lg p-3">
    <div className="min-w-0">
      <div className="font-medium text-sm">{title}</div>
      <p className="text-xs text-muted-foreground mt-1">{desc}</p>
    </div>
    <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} />
  </div>
);
